import jsonExport from "jsonexport/dist";
import {
  downloadCSV,
  InfiniteListBase,
  useGetIdentity,
  useListContext,
  useTranslate,
  type Exporter,
} from "ra-core";
import { BulkActionsToolbar } from "@/components/admin/bulk-actions-toolbar";
import { CreateButton } from "@/components/admin/create-button";
import { ExportButton } from "@/components/admin/export-button";
import { InfiniteList } from "@/components/admin/infinite-list";
import { SortButton } from "@/components/admin/sort-button";

import { FullHeightListLayout } from "../misc/FullHeightListLayout";
import type { Company, Contact, Sale, Tag } from "../types";
import { ContactEmpty } from "./ContactEmpty";
import { ContactImportButton } from "./ContactImportButton";
import { LinkAllToLoopsButton } from "./LinkAllToLoopsButton";
import {
  ContactListContentVirtualized,
  ContactListContentMobile,
} from "./ContactListContent";
import { ContactListFilter } from "./ContactListFilter";
import { useContactFilterBar } from "./contactFilterDescriptors";
import { ActiveFilterBar } from "../misc/ActiveFilterBar";
import { TopToolbar } from "../layout/TopToolbar";
import { InfinitePagination } from "../misc/InfinitePagination";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { ContactApprovalInbox } from "./ContactApprovalInbox";

export const ContactList = () => {
  const { identity } = useGetIdentity();

  if (!identity) return null;

  return (
    <InfiniteList
      title={false}
      actions={<ContactListActions />}
      perPage={50}
      sort={{ field: "last_seen", order: "DESC" }}
      exporter={exporter}
    >
      <ContactListLayoutDesktop />
    </InfiniteList>
  );
};

const ContactListLayoutDesktop = () => {
  const translate = useTranslate();
  const { data, isPending, isFetching, filterValues } = useListContext();
  const filterBar = useContactFilterBar();

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;

  // Wait for a refetch before showing the onboarding screen: removing the last
  // filter from an empty result would otherwise flash it before the rows land.
  if (!data?.length && !hasFilters && !isFetching) {
    return (
      <>
        <ContactApprovalInbox />
        <ContactEmpty />
      </>
    );
  }

  return (
    <>
      <FullHeightListLayout
        sidebar={<ContactListFilter />}
        header={
          <ActiveFilterBar {...filterBar} className="border-b px-4 py-2" />
        }
      >
        <ContactApprovalInbox />
        {data?.length ? (
          <ContactListContentVirtualized />
        ) : (
          <div className="p-4">
            <div className="text-muted-foreground">
              {translate("resources.contacts.empty.title", {})}
            </div>
          </div>
        )}
      </FullHeightListLayout>
      <BulkActionsToolbar />
    </>
  );
};

const ContactListActions = () => (
  <TopToolbar>
    <LinkAllToLoopsButton />
    <SortButton fields={["first_name", "last_name", "last_seen"]} />
    <ContactImportButton />
    <ExportButton exporter={exporter} maxResults={5000} />
    <CreateButton />
  </TopToolbar>
);

export const ContactListMobile = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;

  return (
    <InfiniteListBase
      perPage={25}
      sort={{ field: "last_seen", order: "DESC" }}
      exporter={exporter}
      queryOptions={{
        onError: () => {
          /* Disable error notification as ContactListLayoutMobile handles it */
        },
      }}
    >
      <ContactListLayoutMobile />
    </InfiniteListBase>
  );
};

const ContactListLayoutMobile = () => {
  const { isPending, isFetching, data, error, filterValues } = useListContext();
  const filterBar = useContactFilterBar();

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  // Wait for a refetch before showing the onboarding screen: removing the last
  // filter from an empty result would otherwise flash it before the rows land.
  if (!isPending && !isFetching && !data?.length && !hasFilters) {
    return (
      <>
        <ContactApprovalInbox />
        <ContactEmpty />
      </>
    );
  }

  return (
    <div>
      <MobileHeader>
        <ContactListFilter />
      </MobileHeader>
      <MobileContent>
        <ActiveFilterBar {...filterBar} className="mb-4" />
        <ContactApprovalInbox />
        <div className="flex items-center justify-end gap-1 mb-2">
          <SortButton fields={["first_name", "last_name", "last_seen"]} />
          <ContactImportButton />
          <ExportButton exporter={exporter} maxResults={5000} />
          <CreateButton />
        </div>
        <ContactListContentMobile />
        {!error && (
          <div className="flex justify-center">
            <InfinitePagination />
          </div>
        )}
      </MobileContent>
    </div>
  );
};

const exporter: Exporter<Contact> = async (records, fetchRelatedRecords) => {
  const companies = await fetchRelatedRecords<Company>(
    records,
    "company_id",
    "companies",
  );
  const sales = await fetchRelatedRecords<Sale>(records, "sales_id", "sales");
  const tags = await fetchRelatedRecords<Tag>(records, "tags", "tags");

  const contacts = records.map((contact) => {
    // Guard each related-record lookup: imported contacts may reference a
    // company/sales/tag row that no longer resolves, which would otherwise crash
    // the whole export. sales_id is optional too, so it needs the same null check
    // company_id already had rather than being used as an index directly.
    const sale = contact.sales_id != null ? sales[contact.sales_id] : undefined;
    const exportedContact = {
      ...contact,
      company:
        contact.company_id != null
          ? companies[contact.company_id]?.name
          : undefined,
      sales: sale ? `${sale.first_name} ${sale.last_name}` : undefined,
      tags: (contact.tags ?? [])
        .map((tagId) => tags[tagId]?.name)
        .filter(Boolean)
        .join(", "),
      // One JSON cell. Left as an array, jsonexport writes a bookmarks.label and a
      // bookmarks.url column and an extra CSV row per additional bookmark, which breaks
      // the one-row-per-contact file. Import parses it back. LCATOMICZO-83.
      bookmarks: JSON.stringify(contact.bookmarks ?? []),
    };
    return exportedContact;
  });
  return jsonExport(contacts, {}, (_err: any, csv: string) => {
    downloadCSV(csv, "contacts");
  });
};
